import type { Session } from '@supabase/supabase-js';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { checkWhitelist } from '../lib/authApi';
import { supabase } from '../lib/supabase';

export type AuthStatus =
  | 'initial'
  | 'checking_local'
  | 'checking_session'
  | 'authenticating'
  | 'authenticated'
  | 'unauthenticated'
  | 'logging_out'
  | 'error';

export interface ProcessedSession {
  sessionId: string;
  processedAt: number;
}

export interface PreAuthSnapshot {
  projects: string | null;
  store: string | null;
  currentProjectId: string | null;
  planningState: string | null;
  currentPath?: string | null;
  timestamp: number;
}

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  isLoading: boolean;
  session: Session | null;
  loginMessage: { type: 'success' | 'error'; message: string } | null;
  clearLoginMessage: () => void;
  clearUserData: (userId: string) => void;
  authStatus: AuthStatus;
  hasLocalSession: boolean;
  isReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check if authentication is disabled via environment variable
const isAuthDisabled = () => {
  return import.meta.env.VITE_DISABLE_AUTH === 'true';
};

// Retry utility for network requests
async function retryRequest<T>(
  request: () => Promise<T>,
  maxRetries = 3,
  baseDelay = 1000
): Promise<T> {
  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await request();
    } catch (error) {
      lastError = error as Error;

      if (attempt === maxRetries) {
        throw lastError;
      }

      // Exponential backoff with jitter
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
      console.log(
        `[AuthContext] Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${Math.round(delay)}ms:`,
        error
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

// Helper function to create user-friendly error messages
const createUserFriendlyError = (error: any, operation: string): string => {
  const errorMessage = error?.message || error?.toString() || 'Unknown error';

  // Network-related errors
  if (
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('Network request failed') ||
    errorMessage.includes('TypeError: fetch') ||
    (error?.name === 'TypeError' && errorMessage.includes('fetch'))
  ) {
    return `Unable to connect to the server. Please check your internet connection and try again.`;
  }

  // Timeout errors
  if (errorMessage.includes('timeout') || errorMessage.includes('AbortError')) {
    return `The request is taking too long. Please check your connection and try again.`;
  }

  // CORS or server errors
  if (
    errorMessage.includes('CORS') ||
    errorMessage.includes('405') ||
    errorMessage.includes('500')
  ) {
    return `Server connection issue. Please try again in a moment.`;
  }

  // Auth-specific errors
  if (operation === 'login') {
    if (
      errorMessage.includes('Invalid login credentials') ||
      errorMessage.includes('Authentication failed')
    ) {
      return 'Authentication failed. Please check your email and try again.';
    }
    if (errorMessage.includes('Email not recognised')) {
      return errorMessage; // Keep the original message for whitelist errors
    }
    if (errorMessage.includes('Unable to verify access')) {
      return 'Unable to verify access. Please check your connection and try again.';
    }
  }

  if (operation === 'session_refresh') {
    return 'Your session has expired. Please log in again.';
  }

  if (operation === 'profile_fetch') {
    return 'Unable to load user profile. You can continue, but some features may be limited.';
  }

  // Fallback to original message with connection hint if it seems network-related
  if (
    errorMessage.toLowerCase().includes('network') ||
    errorMessage.toLowerCase().includes('connection') ||
    errorMessage.toLowerCase().includes('internet')
  ) {
    return `${errorMessage} Please check your internet connection.`;
  }

  // Generic fallback
  return `${operation} failed: ${errorMessage}. Please try again.`;
};

// Cache session details in localStorage for quick restoration
const cacheSession = (session: Session | null) => {
  try {
    if (!session) {
      localStorage.removeItem('session-cache');
      return;
    }
    const cache = {
      session,
      expires: Date.now() + session.expires_in * 1000,
    };
    localStorage.setItem('session-cache', JSON.stringify(cache));
  } catch (err) {
    console.error('[AuthContext] Failed to cache session', err);
  }
};

// Retrieve cached session if still valid
const getCachedSession = (): Session | null => {
  try {
    const raw = localStorage.getItem('session-cache');
    if (!raw) return null;
    const { session, expires } = JSON.parse(raw);
    if (!session || !expires || Date.now() > expires) {
      localStorage.removeItem('session-cache');
      return null;
    }
    return session as Session;
  } catch (err) {
    console.error('[AuthContext] Failed to parse cached session', err);
    return null;
  }
};

// Quick session check with timeout to avoid long waits
const checkSession = () =>
  Promise.race([
    supabase.auth.getSession(),
    new Promise<{ data: { session: Session | null }; error: null }>((resolve) =>
      setTimeout(() => resolve({ data: { session: null }, error: null }), 1000)
    ),
  ]);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLocalSession, setHasLocalSession] = useState(false);
  const [loginMessage, setLoginMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const [authStatus, setAuthStatus] = useState<AuthStatus>('initial');
  const [isReady, setIsReady] = useState(false);

  // Track loading states to prevent flickers
  const [_isProcessingAuth, setIsProcessingAuth] = useState(false);
  const processedSessionsRef = useRef<ProcessedSession[]>([]);
  const preAuthSnapshotRef = useRef<PreAuthSnapshot | null>(null);
  const tabIdRef = useRef<string>(
    (() => {
      const existing = sessionStorage.getItem('tab-id');
      if (existing) return existing;
      const id = (crypto as any).randomUUID?.() || Math.random().toString(36).slice(2);
      sessionStorage.setItem('tab-id', id);
      return id;
    })()
  );
  const authChannelRef = useRef<BroadcastChannel | null>(null);

  // Persist session details for faster restoration
  useEffect(() => {
    cacheSession(session);
  }, [session]);

  // Check for local session data immediately
  const checkLocalSession = useCallback(() => {
    if (isAuthDisabled()) {
      setHasLocalSession(true);
      setIsReady(true);
      setIsLoading(false);
      authChannelRef.current?.postMessage({ type: 'auth_ready', tabId: tabIdRef.current });
      return true;
    }

    const cached = getCachedSession();

    if (cached) {
      console.log('[AuthContext] Found cached session, setting immediately');
      setSession(cached);
      setUser({
        id: cached.user.id,
        email: cached.user.email!,
        name: cached.user.email!.split('@')[0],
      });
      markSessionProcessed(cached);
      setAuthStatus('authenticated'); // Set to authenticated since we have a valid cached session
      setIsLoading(false); // Clear loading immediately with cached session
    }

    // Check for session in localStorage/sessionStorage that might indicate a valid session
    const localStorageHasData = !!(
      localStorage.getItem('app-store') ||
      localStorage.getItem('app-projects') ||
      localStorage.getItem(
        'sb-' +
          (import.meta.env.VITE_SUPABASE_URL?.split('//')[1]?.split('.')[0] || 'supabase') +
          '-auth-token'
      ) ||
      cached
    );

    setHasLocalSession(localStorageHasData);

    if (localStorageHasData) {
      setIsReady(true);
      // If we have local data, we can show the app immediately
      if (!cached) {
        setIsLoading(false);
      }
      authChannelRef.current?.postMessage({ type: 'auth_ready', tabId: tabIdRef.current });
    }

    return localStorageHasData;
  }, []);
  // Timer for delayed no-session fallback
  const noSessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasProcessedSession = (s: Session | null) => {
    const id = s?.access_token;
    if (!id) return false;
    return processedSessionsRef.current.some((p) => p.sessionId === id);
  };

  const markSessionProcessed = (s: Session | null) => {
    const id = s?.access_token;
    if (!id) return;
    processedSessionsRef.current.push({ sessionId: id, processedAt: Date.now() });
    if (processedSessionsRef.current.length > 10) {
      processedSessionsRef.current.shift();
    }
  };

  const capturePreAuthSnapshot = async () => {
    try {
      const { useAppStore } = await import('../store/appStore');
      // Persist any in-progress work
      useAppStore.getState().saveCurrentProject();

      const snapshot: PreAuthSnapshot = {
        projects: localStorage.getItem('app-projects'),
        store: localStorage.getItem('app-store'),
        currentProjectId: localStorage.getItem('app-current-project-id'),
        planningState: localStorage.getItem('planningState'),
        currentPath: window.location.hash || null,
        timestamp: Date.now(),
      };

      sessionStorage.setItem('pre-auth-snapshot', JSON.stringify(snapshot));
      preAuthSnapshotRef.current = snapshot;
      console.log('[AuthContext] Captured pre-auth snapshot');
    } catch (err) {
      console.error('[AuthContext] Failed to capture pre-auth snapshot:', err);
    }
  };

  const restorePreAuthSnapshot = async () => {
    const data = sessionStorage.getItem('pre-auth-snapshot');
    if (!data) return;

    try {
      const snapshot: PreAuthSnapshot = JSON.parse(data);
      const { useAppStore } = await import('../store/appStore');
      if (snapshot.projects) {
        const projects = JSON.parse(snapshot.projects);
        useAppStore.getState().mergeProjects(projects);
      }
      if (snapshot.currentPath) {
        window.location.hash = snapshot.currentPath;
      }

      sessionStorage.removeItem('pre-auth-snapshot');
      preAuthSnapshotRef.current = null;
      console.log('[AuthContext] Restored data from pre-auth snapshot');
    } catch (err) {
      console.error('[AuthContext] Failed to restore pre-auth snapshot:', err);
    }
  };

  // Setup BroadcastChannel for cross-tab coordination
  useEffect(() => {
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('auth');
      authChannelRef.current = channel;
      channel.onmessage = (event) => {
        const { type, tabId } = event.data || {};
        if (type === 'login_started' && tabId !== tabIdRef.current) {
          setIsProcessingAuth(true);
        }
        if (type === 'login_complete' && tabId !== tabIdRef.current) {
          setIsProcessingAuth(false);
          sessionStorage.removeItem('primary-auth-tab');
        }
        if (type === 'auth_ready' && tabId !== tabIdRef.current) {
          setIsReady(true);
        }
      };
      return () => channel.close();
    }
    console.warn('[AuthContext] BroadcastChannel not available; auth coordination disabled');
    authChannelRef.current = null;
    return () => {
      /* noop */
    };
  }, []);

  // Session refresh mechanism
  const refreshSession = useCallback(async () => {
    if (!session) return;

    try {
      console.log('[AuthContext] Refreshing session...');
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        console.error('[AuthContext] Session refresh failed:', error);

        // Show user-friendly error message
        const friendlyMessage = createUserFriendlyError(error, 'session_refresh');
        setLoginMessage({ type: 'error', message: friendlyMessage });

        // If refresh fails, clear auth state
        setUser(null);
        setSession(null);
      } else if (data.session) {
        console.log('[AuthContext] Session refreshed successfully');
        setSession(data.session);
        // Clear any previous error messages on successful refresh
        setLoginMessage(null);
      }
    } catch (error) {
      console.error('[AuthContext] Unexpected error during session refresh:', error);
      const friendlyMessage = createUserFriendlyError(error, 'session_refresh');
      setLoginMessage({ type: 'error', message: friendlyMessage });

      // Clear auth state on unexpected errors too
      setUser(null);
      setSession(null);
    }
  }, [session]);

  // Auto-refresh session every 50 minutes (before 60min expiry)
  useEffect(() => {
    if (!session) return;

    const refreshInterval = setInterval(refreshSession, 50 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [session, refreshSession]);

  // Debug logging
  console.log('[AuthContext] Current state:', {
    user: user?.email,
    isLoading,
    hasSession: !!session,
    loginMessage,
  });

  // Initialize auth state
  useEffect(() => {
    setAuthStatus('checking_local');
    // Check for local session immediately
    const hasLocal = checkLocalSession();

    // Auto-login if auth is disabled
    if (isAuthDisabled()) {
      setUser({
        id: 'dev-user',
        email: 'dev@example.com',
        name: 'Development User',
      });
      setIsLoading(false);
      setIsReady(true);
      setAuthStatus('authenticated');
      authChannelRef.current?.postMessage({ type: 'auth_ready', tabId: tabIdRef.current });
      return;
    }

    setAuthStatus('checking_session');

    // Single unified timeout for auth initialization
    // If we have local data, we can show the app immediately while auth loads in background
    const authTimeout = setTimeout(() => {
      if (isLoading && !hasLocal) {
        console.log('[AuthContext] Auth timeout - no local data, showing login');
        setIsLoading(false);
        setIsReady(true);
        setAuthStatus('unauthenticated');
        authChannelRef.current?.postMessage({ type: 'auth_ready', tabId: tabIdRef.current });
      }
    }, 2000); // 2 second timeout for auth without local data

    // Listen for auth changes first so we don't miss events
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (noSessionTimeoutRef.current) {
        clearTimeout(noSessionTimeoutRef.current);
        noSessionTimeoutRef.current = null;
      }
      console.log('[AuthContext] Auth state change:', event, {
        hasSession: !!newSession,
        userId: newSession?.user?.id,
      });

      const primaryTab = sessionStorage.getItem('primary-auth-tab');
      const isPrimary = !primaryTab || primaryTab === tabIdRef.current;

      // Prevent redundant processing of the same session
      if (hasProcessedSession(newSession)) {
        console.log('[AuthContext] Session already processed, skipping');
        return;
      }
      markSessionProcessed(newSession);

      // Set processing flag to prevent UI flickers
      setIsProcessingAuth(true);
      setAuthStatus(newSession ? 'authenticating' : 'unauthenticated');
      setSession(newSession);

      if (newSession?.user) {
        if (!isPrimary) {
          setUser({
            id: newSession.user.id,
            email: newSession.user.email!,
            name: newSession.user.email!.split('@')[0],
          });
          setIsLoading(false);
          setIsProcessingAuth(false);
          setAuthStatus('authenticated');
          return;
        }
        console.log('[AuthContext] Processing new user session...');
        console.log('[AuthContext] Supabase URL:', import.meta.env.VITE_SUPABASE_URL);
        console.log('[AuthContext] Has Anon Key:', !!import.meta.env.VITE_SUPABASE_ANON_KEY);

        try {
          const userData = {
            id: newSession.user.id,
            email: newSession.user.email!,
            name: newSession.user.email!.split('@')[0],
          };

          console.log('[AuthContext] Setting user:', userData);
          setUser(userData);

          // Restore user-specific data on login
          if (event === 'SIGNED_IN') {
            console.log(
              '[AuthContext] User signed in, clearing login message and restoring user data'
            );
            setLoginMessage(null);

            // Use seamless store hydration instead of page reload
            try {
              // Import dynamically to avoid circular dependency
              const { useAppStore } = await import('../store/appStore');
              const hasRestoredData = await useAppStore.getState().hydrateFromUserData(userData.id);

              if (hasRestoredData) {
                console.log('[AuthContext] Successfully hydrated user data without page reload');
              } else {
                console.log('[AuthContext] No user data found to restore');
              }

              await restorePreAuthSnapshot();
            } catch (error) {
              console.error('[AuthContext] Failed to hydrate user data seamlessly:', error);
              // Fallback to the old localStorage restoration method if hydration fails
              try {
                const userStoreData = localStorage.getItem(`app-store-${userData.id}`);
                const userProjectsData = localStorage.getItem(`app-projects-${userData.id}`);
                const userProjectId = localStorage.getItem(`app-current-project-id-${userData.id}`);
                const userPlanningData = localStorage.getItem(`planningState-${userData.id}`);

                if (userStoreData) {
                  localStorage.setItem('app-store', userStoreData);
                  console.log('[AuthContext] Fallback: Restored user store data to localStorage');
                }
                if (userProjectsData) {
                  localStorage.setItem('app-projects', userProjectsData);
                  console.log(
                    '[AuthContext] Fallback: Restored user projects data to localStorage'
                  );
                }
                if (userProjectId) {
                  localStorage.setItem('app-current-project-id', userProjectId);
                  console.log(
                    '[AuthContext] Fallback: Restored user current project to localStorage'
                  );
                }
                if (userPlanningData) {
                  localStorage.setItem('planningState', userPlanningData);
                  console.log(
                    '[AuthContext] Fallback: Restored user planning data to localStorage'
                  );
                }

                // Only reload as last resort if we had to use fallback and data was restored
                if (userStoreData || userProjectsData) {
                  console.log('[AuthContext] Fallback: Data restored without reload');
                }

                await restorePreAuthSnapshot();
              } catch (fallbackError) {
                console.error('[AuthContext] Fallback restoration also failed:', fallbackError);
              }
            }
          }

          // Clear loading state
          console.log('[AuthContext] Clearing loading state');
          setIsLoading(false);
          setIsReady(true);
          authChannelRef.current?.postMessage({ type: 'auth_ready', tabId: tabIdRef.current });

          // Broadcast auth success for any callback tabs
          if (event === 'SIGNED_IN') {
            authChannelRef.current?.postMessage({ type: 'auth_success' });
          }
        } catch (error) {
          console.error('[AuthContext] Error in auth handler:', error);
          // Set user with minimal data to prevent getting stuck
          const userData = {
            id: newSession.user.id,
            email: newSession.user.email!,
            name: newSession.user.email!.split('@')[0],
          };
          setUser(userData);
          setIsLoading(false);
          setIsReady(true);
          authChannelRef.current?.postMessage({ type: 'auth_ready', tabId: tabIdRef.current });
        } finally {
          setIsProcessingAuth(false);
          if (isPrimary) {
            sessionStorage.removeItem('primary-auth-tab');
            authChannelRef.current?.postMessage({
              type: 'login_complete',
              tabId: tabIdRef.current,
            });
          }
          setAuthStatus('authenticated');
        }
      } else {
        console.log('[AuthContext] No session, clearing user and loading');
        setUser(null);
        setIsLoading(false);
        setIsReady(true);
        authChannelRef.current?.postMessage({ type: 'auth_ready', tabId: tabIdRef.current });
        setIsProcessingAuth(false);
        setAuthStatus('unauthenticated');
      }
    });

    // Check for existing Supabase session
    const initializeAuth = async () => {
      console.log('[AuthContext] Initializing auth...');
      let existingSession: Session | null = null;

      try {
        const {
          data: { session },
          error: sessionError,
        } = (await checkSession()) as any;

        existingSession = session;

        if (sessionError) {
          console.error('[AuthContext] Session check error:', sessionError);
        }

        console.log('[AuthContext] Existing session check:', {
          hasSession: !!existingSession,
          userId: existingSession?.user?.id,
        });

        if (existingSession?.user) {
          if (hasProcessedSession(existingSession)) {
            console.log('[AuthContext] Existing session already processed');
            return;
          }
          markSessionProcessed(existingSession);
          setAuthStatus('authenticating');
          console.log('[AuthContext] Found existing session, restoring user');
          setSession(existingSession);
          setUser({
            id: existingSession.user.id,
            email: existingSession.user.email!,
            name: existingSession.user.email!.split('@')[0],
          });

          // Don't set isLoading to false here - let onAuthStateChange handle it
          console.log(
            '[AuthContext] Initial auth complete, waiting for onAuthStateChange to clear loading'
          );
        }
      } catch (error) {
        console.error('[AuthContext] Auth initialization error:', error);
      } finally {
        // If we didn't find a session and don't have local data, set unauthenticated immediately
        if (!existingSession?.user && !hasLocal) {
          console.log('[AuthContext] No existing session or local data, setting unauthenticated');
          setIsLoading(false);
          setAuthStatus('unauthenticated');
          setIsReady(true);
          authChannelRef.current?.postMessage({
            type: 'auth_ready',
            tabId: tabIdRef.current,
          });
        } else if (existingSession?.user) {
          console.log(
            '[AuthContext] Found existing session, letting onAuthStateChange handle loading state'
          );
          // When we have an existing session, we're ready immediately
          setIsReady(true);
          authChannelRef.current?.postMessage({
            type: 'auth_ready',
            tabId: tabIdRef.current,
          });
          // Also set authenticated status immediately to prevent loading screen
          setAuthStatus('authenticated');
        }
      }
    };

    initializeAuth();

    return () => {
      subscription.unsubscribe();
      clearTimeout(authTimeout);
      if (noSessionTimeoutRef.current) {
        clearTimeout(noSessionTimeoutRef.current);
        noSessionTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email: string) => {
    console.log('[AuthContext] Starting login for:', email);
    await capturePreAuthSnapshot();
    sessionStorage.setItem('primary-auth-tab', tabIdRef.current);
    authChannelRef.current?.postMessage({
      type: 'login_started',
      tabId: tabIdRef.current,
    });
    setIsLoading(true);
    setAuthStatus('authenticating');
    setLoginMessage(null); // Clear any previous messages

    try {
      console.log('[AuthContext] Checking email whitelist...');
      const { isWhitelisted } = await retryRequest(() => checkWhitelist(email), 2, 1000);

      if (!isWhitelisted) {
        const message = 'Email not recognised';
        setLoginMessage({ type: 'error', message });
        return { success: false, message };
      }

      console.log('[AuthContext] Sending magic link...');
      // Send magic link with retry
      const magicLinkResult = await retryRequest(
        async () => {
          return supabase.auth.signInWithOtp({
            email,
            options: {
              // Redirect to dedicated auth callback page to prevent login screen flash
              emailRedirectTo: `${window.location.origin}/auth/callback`,
            },
          });
        },
        2,
        1000
      );

      const { error } = magicLinkResult;

      if (error) {
        console.error('[AuthContext] Magic link error:', error);
        throw error; // Let the error handler format this properly
      }

      console.log('[AuthContext] Magic link sent successfully');
      const successMessage =
        'A sign-in link has been sent to your email. It should arrive within a few minutes.';
      setLoginMessage({ type: 'success', message: successMessage });
      return {
        success: true,
        message: successMessage,
      };
    } catch (error: any) {
      console.error('[AuthContext] Login error:', error);
      const friendlyMessage = createUserFriendlyError(error, 'login');
      setLoginMessage({ type: 'error', message: friendlyMessage });
      return {
        success: false,
        message: friendlyMessage,
      };
    } finally {
      console.log('[AuthContext] Login process complete, clearing loading state');
      setIsLoading(false);
      setAuthStatus('unauthenticated');
    }
  }, []);

  const logout = useCallback(async () => {
    console.log('[AuthContext] Logging out...');
    setAuthStatus('logging_out');

    // Persist any in-progress project state before clearing storage
    try {
      const { useAppStore } = await import('../store/appStore');
      useAppStore.getState().saveCurrentProject();
    } catch (err) {
      console.error('[AuthContext] Failed to save project on logout:', err);
    }

    if (user?.id) {
      console.log('[AuthContext] Preserving user data for:', user.email);
      // Save current user's data to user-specific localStorage keys before logout
      try {
        const currentData = localStorage.getItem('app-store');
        const currentProjects = localStorage.getItem('app-projects');
        const currentProjectId = localStorage.getItem('app-current-project-id');
        const currentPlanning = localStorage.getItem('planningState');

        if (currentData) {
          localStorage.setItem(`app-store-${user.id}`, currentData);
        }
        if (currentProjects) {
          localStorage.setItem(`app-projects-${user.id}`, currentProjects);
        }
        if (currentProjectId) {
          localStorage.setItem(`app-current-project-id-${user.id}`, currentProjectId);
        }
        if (currentPlanning) {
          localStorage.setItem(`planningState-${user.id}`, currentPlanning);
        }

        console.log('[AuthContext] User data preserved for future login');
      } catch (error) {
        console.error('[AuthContext] Failed to preserve user data:', error);
      }
    }

    // Clear local state immediately
    setUser(null);
    setSession(null);
    setLoginMessage(null);
    setIsLoading(false);

    try {
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[AuthContext] Logout error:', error);
      }

      // Clear general localStorage but keep user-specific data
      localStorage.removeItem('app-store');
      localStorage.removeItem('app-projects');
      localStorage.removeItem('app-current-project-id');
      localStorage.removeItem('planningState');

      // Navigate back to login without forcing a full reload
      window.location.assign('/');
    } catch (error) {
      console.error('[AuthContext] Unexpected logout error:', error);
      // Even if there's an error, force navigation to ensure clean state
      window.location.assign('/');
    }
    setAuthStatus('unauthenticated');
  }, [user]);

  const clearLoginMessage = useCallback(() => {
    setLoginMessage(null);
  }, []);

  const clearUserData = useCallback((userId: string) => {
    console.log('[AuthContext] Clearing stored data for user:', userId);
    try {
      localStorage.removeItem(`app-store-${userId}`);
      localStorage.removeItem(`app-projects-${userId}`);
      localStorage.removeItem(`app-current-project-id-${userId}`);
      localStorage.removeItem(`planningState-${userId}`);
      console.log('[AuthContext] User data cleared successfully');
    } catch (error) {
      console.error('[AuthContext] Failed to clear user data:', error);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        isLoading,
        session,
        loginMessage,
        clearLoginMessage,
        clearUserData,
        authStatus,
        hasLocalSession,
        isReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
